/**
 * Default Route Component for TECDocChart AI Client & EMR Routing
 * 
 * Implements smart routing based on single_user configuration to resolve Issue #94:
 * - single_user = true: Routes to encounters list for AI-assisted charting
 * - single_user = false: Routes to dashboard for EMR overview
 * 
 * Healthcare Compliance: Preserves existing authentication and audit patterns
 * Clinical Impact: Restores AI client workflow for medical professionals
 */

import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Loading from '../shared/Loading';

interface DefaultRouteProps {
  fallbackRoute?: string;
  logRouting?: boolean;
}

interface AppContext {
  singleUser?: boolean;
  version?: string;
  environment?: string;
  [key: string]: any;
}

function DefaultRoute({
  fallbackRoute = '/dashboard',
  logRouting = false
}: DefaultRouteProps) {
  const [routeDecision, setRouteDecision] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    determineRoute();
  }, []);

  const determineRoute = async () => {
    try {
      // Access global context from Django template (same pattern as ProtectedRoute)
      const global = (window as any);
      const appContext: AppContext = global.appContext;
      const authContext = global.authContext;

      if (!appContext) {
        if (logRouting) {
          console.warn('DefaultRoute: appContext not found, using fallback route:', fallbackRoute);
        }
        setRouteDecision(fallbackRoute);
        setLoading(false);
        return;
      }

      // CRITICAL: Check authentication first - redirect to login if not authenticated
      const isAuthenticated = authContext?.authenticated ?? false;
      if (!isAuthenticated) {
        if (logRouting) {
          console.log('DefaultRoute: User not authenticated → redirecting to login');
        }
        setRouteDecision('/login');
        setLoading(false);
        return;
      }

      // Determine route based on single_user configuration
      const singleUser = appContext.singleUser ?? false;
      let targetRoute: string;

      if (singleUser) {
        // AI Client: Route to the React TECdoc Scribe patient list
        targetRoute = '/ai/list';
        if (logRouting) {
          console.log('DefaultRoute: AI Client detected (single_user=true) → routing to React AI client');
        }
      } else {
        const employee = authContext?.employee;
        const permissions: string[] = employee?.permissions || authContext?.permissions || [];
        const canViewQualityDashboard =
          authContext?.user?.is_superuser || permissions.includes('quality_dashboard');

        // Employee.dashboard is the established default-dashboard preference.
        targetRoute =
          employee?.dashboard === 'quality' && canViewQualityDashboard
            ? '/dashboard/#/quality'
            : '/dashboard';
        if (logRouting) {
          console.log('DefaultRoute: EMR Client detected (single_user=false) → routing to preferred dashboard');
        }
      }

      // Log configuration details for debugging
      if (logRouting) {
        console.log('DefaultRoute configuration:', {
          singleUser: singleUser,
          environment: appContext.environment,
          version: appContext.version,
          targetRoute: targetRoute,
          authenticated: isAuthenticated
        });
      }

      setRouteDecision(targetRoute);
      setLoading(false);

    } catch (error) {
      console.error('DefaultRoute: Error determining route:', error);
      setError(`Route determination failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setRouteDecision(fallbackRoute);
      setLoading(false);
    }
  };

  // Show loading state while determining route
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <Loading message="Determining route..." />
      </div>
    );
  }

  // Show error state if route determination failed
  if (error) {
    console.error('DefaultRoute error, falling back to:', fallbackRoute);
    return <Navigate to={fallbackRoute} replace />;
  }

  // Navigate to determined route
  if (routeDecision) {
    // Check if this is an external route (outside React shell).
    // /ai/list is now a React route, so only hash-based AngularJS URLs (e.g.
    // the not-yet-converted /ai/#/ai/user/:id) still need a full page load.
    // The /encounters/main arm was dropped: 1e7c694 removed that route, so the clause
    // could only ever full-load a 404. Anything stray now falls through to React Router.
    if (routeDecision.includes('#')) {
      // Use window.location for external navigation (AngularJS routes)
      window.location.href = routeDecision;
      const redirectMessage = routeDecision.startsWith('/ai') ? 'Redirecting to AI client...' : 'Redirecting to encounters...';
      return <Loading message={redirectMessage} />;
    } else {
      // Use React Router for internal navigation (dashboard, etc.)
      return <Navigate to={routeDecision} replace />;
    }
  }

  // Final fallback
  return <Navigate to={fallbackRoute} replace />;
}

export default DefaultRoute;
